import React, { useEffect } from "react"
import * as Sentry from "@sentry/react"
import { getStaticFeatures } from "../hooks/use-feature"

const allowedHosts = ["app.kitstag.com", "app.kit-qa.com", "app.kit.com"]

const isE2ETest = process.env.CI_E2E_TEST === "true"
const isDeployableEnv = ["production", "staging", "qa"].includes(
  process.env.BUGSNAG_ENV || process.env.NODE_ENV
)

const shouldReportError =
  isDeployableEnv && !isE2ETest && allowedHosts.includes(window.location.host)

if (shouldReportError) {
  Sentry.init({
    dsn: "https://78da087cb2326bd6555d132490a7adbe@o4508813230211072.ingest.us.sentry.io/4510108386590720",
    sendDefaultPii: true,
    release: process.env.SHORT_CIRCLE_SHA1,
    environment: process.env.BUGSNAG_ENV || process.env.NODE_ENV,
    enabled: shouldReportError,
    integrations: [
      Sentry.browserTracingIntegration({
        // Only create spans for API requests, not static assets from CDN
        shouldCreateSpanForRequest: url => {
          // Exclude CDN domains (JS chunks, static assets)
          if (
            url.includes("cdn.kit.com") ||
            url.includes("cdn.kitstag.com") ||
            url.includes("cdn.kit-qa.com") ||
            url.includes("graph.facebook.com")
          ) {
            return false
          }
          // Include our app domains
          return (
            url.includes("kit.com") ||
            url.includes("kitstag.com") ||
            url.includes("kit-qa.com") ||
            url.includes("filekitcdn.com")
          )
        }
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true, // Keep passwords/credit cards masked
        blockAllMedia: false
      })
    ],

    // Tracing
    tracesSampleRate: 0.01,

    // Only propagate trace headers to our app domains (not third-party APIs)
    // NOTE: Keep tracePropagationTargets and beforeSend filters in sync with app/views/layouts/application.html.erb
    tracePropagationTargets: [
      /^https:\/\/app\.kit\.com/,
      /^https:\/\/app\.kitstag\.com/,
      /^https:\/\/app\.kit-qa\.com/,
      /^https:\/\/api\.filekitcdn\.com/,
      /^https:\/\/h\.staging-convertkitfiles\.com/,
      /^https:\/\/h\.qa-convertkitfiles\.com/
    ],

    // Session Replay
    replaysSessionSampleRate: 0.001,
    replaysOnErrorSampleRate: 0.05,

    beforeSend(event, hint) {
      const error = hint.originalException

      // Filter out specific errors (check both originalException and event.exception.values)
      const errorName = error?.name || event.exception?.values?.[0]?.type || ""
      const errorValue =
        error?.message || event.exception?.values?.[0]?.value || ""

      if (errorName === "InvalidError") {
        if (
          errorValue.startsWith("Object Not Found Matching Id:") ||
          errorValue.includes("unhandledrejection handler received a non-error")
        ) {
          return null
        }
      }

      // Filter Sentry SDK noise: non-error promise rejections
      // These occur when code does Promise.reject("string") instead of Promise.reject(new Error("string"))
      // Sentry wraps these and creates synthetic errors - they're SDK noise, not real app errors
      const errorMessage =
        error?.message || event.exception?.values?.[0]?.value || ""
      if (
        errorMessage.includes(
          "Non-Error promise rejection captured with value:"
        )
      ) {
        return null
      }

      // Filter errors from browser extensions or Safari privacy features
      // webkit-masked-url://hidden indicates masked extension/tracking code, not our app
      if (
        event.exception?.values?.some(v =>
          v.stacktrace?.frames?.some(f =>
            f.filename?.includes("webkit-masked-url://hidden")
          )
        )
      ) {
        return null
      }

      // Filter noisy network-related failures
      const message = (error?.message || "").toLowerCase()
      if (
        (error?.name === "TypeError" &&
          message.toLowerCase().includes("load failed")) ||
        (error?.name === "TypeError" &&
          message.toLowerCase().includes("failed to fetch")) ||
        (error?.name === "AxiosError" &&
          message.toLowerCase().includes("network error"))
      ) {
        return null
      }

      // Track errors to analytics
      if (window.analytics) {
        const user = window.app?.user

        window.analytics.track("error", {
          path: window.location.pathname.replace(/\d+/g, ":id"),
          context: event.contexts,
          user_id: user?.id,
          account_id: user?.account_id,
          class_name: error?.name
        })
      }

      // Attach static features
      const features = getStaticFeatures()
      if (features.length > 0) {
        event.contexts = event.contexts || {}
        event.contexts.features = features.reduce(
          (acc, feat) => ({ ...acc, [feat]: true }),
          {}
        )
      }

      return event
    }
  })
}

// Provide fallback ErrorBoundary when Sentry is not initialized
const ErrorBoundary = !shouldReportError
  ? ({ children }) => children
  : Sentry.ErrorBoundary

export function SentryErrorBoundary({ children }) {
  useEffect(() => {
    // Only set user data if Sentry is initialized
    if (!shouldReportError || !window.app) {
      return
    }
    const user = window.app.user

    // Set user identification in Sentry
    Sentry.setUser({
      id: user?.id,
      email: user?.email
    })

    // Add account metadata
    Sentry.setContext("account", {
      id: user?.account_id,
      created_at: user?.account_created_at
    })

    // Add admin context if impersonating
    if (user?.admin) {
      Sentry.setContext("admin", {
        id: window.app.admin_user?.id,
        email: window.app.admin_user?.email
      })
    }
  }, [])

  return <ErrorBoundary>{children}</ErrorBoundary>
}

window.SentryClient = Sentry
export { Sentry }
